import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../helpers/apiUtils';
import moment from 'moment-timezone';
import { formatGermanTime, fromGermanDatetimeLocal } from '../helpers/germanTime';

/**
 * NonOpenerSection Component
 * Allows scheduling follow-up sends to users who didn't open original mailings
 * Used in both PLX and Webinar CampaignEditor
 */

export type SystemType = 'plx' | 'webinar';
export type NonOpenerStatus = 'pending' | 'scheduled' | 'sent' | 'failed' | 'cancelled';

export interface EligibleSchedule {
  schedule_id: number;
  email_id: number;
  client_code: string;
  client_name: string;
  esp_type: 'iterable' | 'mailchimp';
  esp_campaign_id: string;
  scheduled_datetime: string;
  sent_at: string | null;
  status: string;
  subject: string;
  from_name: string;
  mailing_number: number;
  // Non-opener status (if already scheduled)
  non_opener_id: number | null;
  non_opener_status: NonOpenerStatus | null;
  non_opener_scheduled_datetime: string | null;
  non_opener_error_message: string | null;
}

export interface NonOpenerSectionProps {
  systemType: SystemType;
  campaignId: number | null;
  emails: Array<{
    mailing_number: number;
    subject: string;
    selected_clients: string[];
  }>;
  onNonOpenerScheduled?: () => void;
}

interface GroupedSchedules {
  [mailingNumber: number]: EligibleSchedule[];
}

const NonOpenerSection: React.FC<NonOpenerSectionProps> = ({
  systemType,
  campaignId,
  emails,
  onNonOpenerScheduled,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [eligibleSchedules, setEligibleSchedules] = useState<EligibleSchedule[]>([]);
  const [expandedPanel, setExpandedPanel] = useState<number | false>(false);

  // Per-email scheduling state
  const [selectedClients, setSelectedClients] = useState<Record<number, string[]>>({});
  const [scheduledDatetimes, setScheduledDatetimes] = useState<Record<number, string>>({});
  const [scheduling, setScheduling] = useState<Record<number, boolean>>({});

  // Load eligible schedules when campaignId changes
  const loadEligibleSchedules = useCallback(async () => {
    if (!campaignId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchData<EligibleSchedule[]>(
        `/api/non-opener/${systemType}/${campaignId}/eligible`,
      );

      if (response.success && response.data) {
        setEligibleSchedules(response.data);

        // Initialize default scheduled datetimes (original time + 6 hours)
        const defaultDatetimes: Record<number, string> = {};
        const defaultClients: Record<number, string[]> = {};

        // Group by mailing_number to find latest original time per group
        const schedulesByMailing: Record<number, EligibleSchedule[]> = {};
        response.data.forEach((schedule) => {
          if (!schedulesByMailing[schedule.mailing_number]) {
            schedulesByMailing[schedule.mailing_number] = [];
          }
          schedulesByMailing[schedule.mailing_number].push(schedule);
        });

        Object.entries(schedulesByMailing).forEach(([mailingNumStr, schedules]) => {
          const mailingNumber = parseInt(mailingNumStr, 10);

          // Find the latest original send time across all clients
          // DB DATETIME values are Europe/Berlin time; with timezone "+00:00"
          // they arrive as UTC-tagged but the numeric part IS already Berlin time.
          let latestOriginal = moment.utc('1970-01-01');
          schedules.forEach((s) => {
            const t = moment.utc(s.sent_at || s.scheduled_datetime);
            if (t.isAfter(latestOriginal)) {
              latestOriginal = t;
            }
          });

          // Default: same day 19:00
          const defaultTime = latestOriginal.clone().set({ hour: 19, minute: 0, second: 0 });
          // If 19:00 is less than 6h after latest send, use +6h instead
          const sixHoursAfter = latestOriginal.clone().add(6, 'hours');
          if (defaultTime.isBefore(sixHoursAfter)) {
            defaultTime
              .year(sixHoursAfter.year())
              .month(sixHoursAfter.month())
              .date(sixHoursAfter.date())
              .hour(sixHoursAfter.hour())
              .minute(sixHoursAfter.minute());
          }
          // If result is in the past, next day 19:00
          if (defaultTime.isBefore(moment())) {
            defaultTime.add(1, 'day').set({ hour: 19, minute: 0 });
          }

          defaultDatetimes[mailingNumber] = defaultTime.format('YYYY-MM-DDTHH:mm');
          defaultClients[mailingNumber] = [];
        });

        setScheduledDatetimes((prev) => ({ ...prev, ...defaultDatetimes }));
        setSelectedClients((prev) => ({ ...prev, ...defaultClients }));
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, systemType]);

  useEffect(() => {
    loadEligibleSchedules();
  }, [loadEligibleSchedules]);

  // Group schedules by mailing number
  const groupedSchedules: GroupedSchedules = {};
  eligibleSchedules.forEach((schedule) => {
    if (!groupedSchedules[schedule.mailing_number]) {
      groupedSchedules[schedule.mailing_number] = [];
    }
    groupedSchedules[schedule.mailing_number].push(schedule);
  });

  // Handle client selection toggle
  const handleClientToggle = (mailingNumber: number, clientCode: string) => {
    setSelectedClients((prev) => {
      const current = prev[mailingNumber] || [];
      if (current.includes(clientCode)) {
        return {
          ...prev,
          [mailingNumber]: current.filter((c) => c !== clientCode),
        };
      } else {
        return { ...prev, [mailingNumber]: [...current, clientCode] };
      }
    });
  };

  // Handle datetime change
  const handleDatetimeChange = (mailingNumber: number, value: string) => {
    setScheduledDatetimes((prev) => ({ ...prev, [mailingNumber]: value }));
  };

  // Schedule non-opener sends for a specific email
  const handleSchedule = async (mailingNumber: number) => {
    const clients = selectedClients[mailingNumber] || [];
    const datetime = scheduledDatetimes[mailingNumber];

    if (clients.length === 0) {
      setError('Bitte mindestens einen Client auswählen');
      return;
    }

    if (!datetime) {
      setError('Bitte Versandzeitpunkt auswählen');
      return;
    }

    // Validate datetime is in future
    if (fromGermanDatetimeLocal(datetime).isBefore(moment())) {
      setError('Versandzeitpunkt muss in der Zukunft liegen');
      return;
    }

    setScheduling((prev) => ({ ...prev, [mailingNumber]: true }));
    setError(null);
    setSuccess(null);

    try {
      const schedules = groupedSchedules[mailingNumber] || [];
      const results: {
        client: string;
        success: boolean;
        deferred?: boolean;
        error?: string;
      }[] = [];

      // Schedule each selected client
      for (const clientCode of clients) {
        const schedule = schedules.find((s) => s.client_code === clientCode);
        if (!schedule) continue;

        // Skip if already has non-opener scheduled
        if (
          schedule.non_opener_id &&
          schedule.non_opener_status !== 'cancelled' &&
          schedule.non_opener_status !== 'failed'
        ) {
          results.push({
            client: clientCode,
            success: false,
            error: 'Bereits geplant',
          });
          continue;
        }

        try {
          const response = (await fetchData(`/api/non-opener/${systemType}/schedule`, {
            method: 'POST',
            body: JSON.stringify({
              originalScheduleId: schedule.schedule_id,
              clientCode,
              scheduledDatetime: datetime,
            }),
          })) as { success: boolean; deferred?: boolean; error?: string };

          if (response.deferred) {
            results.push({ client: clientCode, success: true, deferred: true });
          } else if (response.success) {
            results.push({ client: clientCode, success: true });
          } else {
            results.push({
              client: clientCode,
              success: false,
              error: response.error,
            });
          }
        } catch (err: any) {
          results.push({
            client: clientCode,
            success: false,
            error: err.message,
          });
        }
      }

      // Show results
      const successful = results.filter((r) => r.success);
      const deferred = results.filter((r) => r.deferred);
      const failed = results.filter((r) => !r.success);

      if (successful.length > 0 && failed.length === 0) {
        if (deferred.length > 0 && deferred.length === successful.length) {
          // All were deferred
          setSuccess(
            `Non-Opener wird nach dem Original-Versand automatisch geplant für: ${deferred.map((r) => r.client).join(', ')}`,
          );
        } else if (deferred.length > 0) {
          // Mix of immediate + deferred
          const immediate = successful.filter((r) => !r.deferred);
          setSuccess(
            `Non-Opener geplant für: ${immediate.map((r) => r.client).join(', ')}. Vorgemerkt (Original noch nicht gesendet): ${deferred.map((r) => r.client).join(', ')}`,
          );
        } else {
          setSuccess(
            `Non-Opener erfolgreich geplant für: ${successful.map((r) => r.client).join(', ')}`,
          );
        }
        setSelectedClients((prev) => ({ ...prev, [mailingNumber]: [] }));
        onNonOpenerScheduled?.();
        loadEligibleSchedules();
      } else if (successful.length > 0) {
        setSuccess(`Teilweise erfolgreich: ${successful.map((r) => r.client).join(', ')}`);
        setError(`Fehlgeschlagen: ${failed.map((r) => `${r.client}: ${r.error}`).join(', ')}`);
        loadEligibleSchedules();
      } else {
        setError(`Fehler: ${failed.map((r) => `${r.client}: ${r.error}`).join(', ')}`);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setScheduling((prev) => ({ ...prev, [mailingNumber]: false }));
    }
  };

  // Cancel a non-opener send
  const handleCancel = async (nonOpenerSendId: number) => {
    if (!window.confirm('Non-Opener Versand abbrechen?')) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchData(`/api/non-opener/${nonOpenerSendId}`, {
        method: 'DELETE',
      });

      if (response.success) {
        setSuccess('Non-Opener Versand abgebrochen');
        loadEligibleSchedules();
      } else {
        setError(response.error || 'Fehler beim Abbrechen');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render status chip for non-opener
  const renderNonOpenerStatus = (schedule: EligibleSchedule) => {
    if (!schedule.non_opener_id) return null;

    const statusColors: Record<
      NonOpenerStatus,
      'default' | 'primary' | 'success' | 'error' | 'warning'
    > = {
      pending: 'warning',
      scheduled: 'primary',
      sent: 'success',
      failed: 'error',
      cancelled: 'default',
    };

    const statusLabels: Record<NonOpenerStatus, string> = {
      pending: 'Wartend',
      scheduled: 'Geplant',
      sent: 'Gesendet',
      failed: 'Fehler',
      cancelled: 'Abgebrochen',
    };

    const status = schedule.non_opener_status || 'pending';

    const chip = (
      <Chip
        size="small"
        label={`Non-Opener: ${statusLabels[status]}`}
        color={statusColors[status]}
        icon={
          status === 'scheduled' ? (
            <ScheduleIcon />
          ) : status === 'sent' ? (
            <CheckCircleIcon />
          ) : undefined
        }
      />
    );

    return (
      <Box display="flex" alignItems="center" gap={1}>
        {status === 'failed' && schedule.non_opener_error_message ? (
          <Tooltip title={schedule.non_opener_error_message} arrow>
            {chip}
          </Tooltip>
        ) : (
          chip
        )}
        {schedule.non_opener_scheduled_datetime && (
          <Typography variant="caption" color="text.secondary">
            {formatGermanTime(schedule.non_opener_scheduled_datetime!, 'DD.MM. HH:mm')}
          </Typography>
        )}
        {['pending', 'scheduled', 'failed'].includes(status) && schedule.non_opener_id && (
          <Tooltip
            title={
              status === 'failed' ? 'Fehlgeschlagenen Non-Opener löschen' : 'Non-Opener abbrechen'
            }
          >
            <Button
              size="small"
              color="error"
              onClick={() => handleCancel(schedule.non_opener_id!)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <CancelIcon fontSize="small" />
            </Button>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Check if schedule is eligible for non-opener (not already scheduled)
  const isEligibleForNonOpener = (schedule: EligibleSchedule): boolean => {
    if (
      schedule.non_opener_id &&
      schedule.non_opener_status !== 'cancelled' &&
      schedule.non_opener_status !== 'failed'
    ) {
      return false;
    }
    return true;
  };

  // No campaign or no eligible schedules
  if (!campaignId) {
    return null;
  }

  if (loading && eligibleSchedules.length === 0) {
    return (
      <Box p={2} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography>Lade Non-Opener Daten...</Typography>
      </Box>
    );
  }

  if (eligibleSchedules.length === 0) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Keine E-Mails für Non-Opener Versand verfügbar. E-Mails müssen geplant oder innerhalb der
          letzten 24 Stunden gesendet worden sein.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Non-Opener Versand</Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadEligibleSchedules}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Non-Opener werden an Empfänger gesendet, die die Original-E-Mail erhalten aber nicht
          geöffnet haben. Der Banner erhält automatisch das Suffix &quot;_NonOpener&quot; für
          separates Tracking.
        </Typography>
      </Alert>

      {/* Warning for Iterable (FT) - opener data delay */}
      {eligibleSchedules.some((s) => s.esp_type === 'iterable') && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            <strong>Hinweis für FT (Iterable):</strong> Öffnungs-Daten können bis zu 4 Stunden
            dauern. Für beste Ergebnisse Non-Opener frühestens 4-6 Stunden nach dem Original-Versand
            planen.
          </Typography>
        </Alert>
      )}

      {/* Accordion per email */}
      {Object.entries(groupedSchedules).map(([mailingNumberStr, schedules]) => {
        const mailingNumber = parseInt(mailingNumberStr, 10);
        const email = emails.find((e) => e.mailing_number === mailingNumber);
        const datetime = scheduledDatetimes[mailingNumber] || '';
        const selected = selectedClients[mailingNumber] || [];
        const isScheduling = scheduling[mailingNumber] || false;

        // Get eligible clients (not already scheduled for non-opener)
        const eligibleClients = schedules.filter((s: EligibleSchedule) =>
          isEligibleForNonOpener(s),
        );
        const scheduledNonOpeners = schedules.filter(
          (s: EligibleSchedule) => !isEligibleForNonOpener(s),
        );

        return (
          <Accordion
            key={mailingNumber}
            expanded={expandedPanel === mailingNumber}
            onChange={(_, expanded) => setExpandedPanel(expanded ? mailingNumber : false)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography fontWeight="bold">E-Mail #{mailingNumber}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {email?.subject || 'Kein Betreff'}
                </Typography>
                {scheduledNonOpeners.length > 0 && (
                  <Chip
                    size="small"
                    label={`${scheduledNonOpeners.length} Non-Opener geplant`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Original send info */}
              <Typography variant="subtitle2" gutterBottom>
                Original-Versand:
              </Typography>
              <Box mb={2}>
                {schedules.map((schedule: EligibleSchedule) => (
                  <Box
                    key={schedule.schedule_id}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    py={0.5}
                    px={1}
                    sx={{
                      backgroundColor: schedule.status === 'sent' ? '#e8f5e9' : '#fff3e0',
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <Chip size="small" label={schedule.client_name} variant="outlined" />
                    <Typography variant="body2">
                      {schedule.status === 'sent' ? 'Gesendet' : 'Geplant'}:{' '}
                      {formatGermanTime(
                        schedule.sent_at || schedule.scheduled_datetime,
                        'DD.MM.YYYY HH:mm',
                      )}
                    </Typography>
                    {renderNonOpenerStatus(schedule)}
                  </Box>
                ))}
              </Box>

              {eligibleClients.length > 0 ? (
                <>
                  {/* Client selection */}
                  <Typography variant="subtitle2" gutterBottom>
                    Non-Opener versenden an:
                  </Typography>
                  <FormGroup row sx={{ mb: 2 }}>
                    {eligibleClients.map((schedule: EligibleSchedule) => (
                      <FormControlLabel
                        key={schedule.client_code}
                        control={
                          <Checkbox
                            checked={selected.includes(schedule.client_code)}
                            onChange={() => handleClientToggle(mailingNumber, schedule.client_code)}
                            disabled={isScheduling}
                          />
                        }
                        label={`${schedule.client_name} (${schedule.esp_type}) — ${schedule.status === 'sent' ? 'Gesendet' : 'Geplant'}: ${formatGermanTime(schedule.sent_at || schedule.scheduled_datetime, 'DD.MM. HH:mm')}`}
                      />
                    ))}
                  </FormGroup>

                  {/* Datetime picker */}
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <TextField
                      type="datetime-local"
                      label="Versandzeitpunkt (MEZ/MESZ)"
                      value={datetime}
                      onChange={(e) => handleDatetimeChange(mailingNumber, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      disabled={isScheduling}
                      sx={{ minWidth: 250 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isScheduling ? <CircularProgress size={16} /> : <ScheduleIcon />}
                      onClick={() => handleSchedule(mailingNumber)}
                      disabled={isScheduling || selected.length === 0 || !datetime}
                    >
                      {isScheduling ? 'Wird geplant...' : 'Non-Opener planen'}
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Alle Clients haben bereits Non-Opener geplant.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default NonOpenerSection;
